import { isValidElement, useState } from "react";
import {
    Backdrop,
    Paper,
    Stack,
    Typography,
    Divider,
    Grid,
} from "@mui/material";

import CustomModalHeader from "../custom_components/CustomModalHeader";
import { CustomImage, PreviewImage } from "../../utils/ImageKit";

import { chipOrderStatus } from "../../utils/chipOrderStatus";
import { chipPaymentStatus } from "../../utils/chipPaymentStatus";
import { formatToLongDate } from "../../utils/formatToLongDate";

const InfoRow = ({ label, value }) => (
    <Stack spacing={0.5}>
        <Typography
            sx={{
                fontSize: 12,
                color: "#6B7280",
                fontWeight: 600,
            }}
        >
            {label}
        </Typography>

        {isValidElement(value) ? (
            value
        ) : (
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#111827",
                }}
            >
                {value || "--"}
            </Typography>
        )}
    </Stack>
);

export default function OrderInfo({ open, onClose, order, }) {

    const [openPreviewImg, setOpenPreviewImg] = useState(false);

    if (!order) return null;

    return (
        <Backdrop
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Paper
                sx={{
                    width: 850,
                    maxWidth: "95vw",
                    p: 3,
                    borderRadius: 1.2,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#ffffff",

                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >
                {/* HEADER */}
                <CustomModalHeader
                    title="Order Information"
                    subtitle="View complete order details"
                    onClose={onClose}
                />

                {/* CUSTOMER */}
                <Grid
                    container
                    spacing={3}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid>
                        <Typography
                            fontWeight={700}
                            mb={1}
                        >
                            Customer Details
                        </Typography>
                        
                        <Grid container spacing={2}>
                            <Grid>
                                <InfoRow
                                    label="Customer ID"
                                    value={order.CustomerID}
                                />
                            </Grid>
                            <Grid>
                                <InfoRow
                                    label="Customer Name"
                                    value={order.CustomerName}
                                />
                            </Grid>
                            <Grid>
                                <InfoRow
                                    label="Second Name"
                                    value={order.SecondName}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid
                        sx={{
                            display: "flex",
                            justifyContent: {
                                xs: "center",
                                md: "flex-end"
                            }
                        }}
                    >
                        <CustomImage
                            src={order.ItemImg}
                            width={120}
                            height={120}
                            onClick={() => setOpenPreviewImg(true)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* ORDER */}
                <Typography
                    fontWeight={700}
                    mb={1}
                >
                    Order Details
                </Typography>

                <Grid container spacing={3}>
                    <Grid>
                        <InfoRow
                            label="Order ID"
                            value={order.OrderID}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Date Ordered"
                            value={formatToLongDate(order.DateOrdered)}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Item Name"
                            value={order.ItemName}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Category"
                            value={order.ItemCategory}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Quantity"
                            value={`${order.ItemQty}x`}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Cargo"
                            value={order.Cargo}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Order Status"
                            value={chipOrderStatus(order.OrderStat)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* PAYMENT */}
                <Typography
                    fontWeight={700}
                    mb={1}
                >
                    Payment Details
                </Typography>

                <Grid container spacing={3}>
                    <Grid>
                        <InfoRow
                            label="Price"
                            value={`₱${Number(order.Price).toLocaleString()}`}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Shipping Fee"
                            value={`₱${Number(order.SFee).toLocaleString()}`}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Total Amount"
                            value={`₱${(
                                Number(order.Price) *
                                Number(order.ItemQty) +
                                Number(order.SFee)
                            ).toLocaleString()}`}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Paid Amount"
                            value={`₱${Number(order.PaidAmount || 0).toLocaleString()}`}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Balance"
                            value={`₱${Number(order.Balance || 0).toLocaleString()}`}
                        />
                    </Grid>

                    <Grid>
                        <InfoRow
                            label="Payment Status"
                            value={chipPaymentStatus(order.PayStatus)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* List At */}
                <Typography
                    fontWeight={700}
                    mb={1}
                >
                    Listed At
                </Typography>

                <Stack spacing={1}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: order.ListName
                                ? "#000000"
                                : "#94A3B8",
                        }}
                    >
                        {order.ListName || order.DateCreated ? (
                            <>
                                {order.ListName || "--"} {" • "} {order.DateCreated
                                    ? formatToLongDate(order.DateCreated)
                                    : "--"
                                }
                            </>
                        ) : (
                            <em>Unlisted</em>
                        )}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: order.ArrivedListName
                                ? "#000000"
                                : "#94A3B8",
                        }}
                    >
                        {order.ArrivedListName || order.DateArrived ? (
                            <>
                                {order.ArrivedListName || "--"} {" • "} {order.DateArrived
                                    ? formatToLongDate(order.DateArrived)
                                    : "--"
                                }
                            </>
                        ) : (
                            <em>No yet arrived</em>
                        )}
                    </Typography>
                </Stack>

                <PreviewImage
                    open={openPreviewImg}
                    onClose={() => setOpenPreviewImg(false)}
                    src={order.ItemImg}
                />

            </Paper>
        </Backdrop>
    );
}